/**
 * B966 — two departures at one wall-clock time (two vessels, two routes)
 * were the same tile to a buyer: "10:30 AM · 12 left" beside "10:30 AM ·
 * 10 left", and a screen reader heard the same words twice. A tile that
 * shares its time with another on the list earns ONE distinguishing fact
 * under the time: the length when every twin's differs, else its place
 * among them ("1 of 2"). A lone time earns nothing — the common day keeps
 * its face. The slot summary carries no vessel or route name (schedules are
 * unnamed), so these are the facts the wire honestly has.
 */

const MINUTE = 60_000;

/** "45 min" · "2 h" · "1 h 15 min". */
export function durationWords(ms: number): string {
  const total = Math.round(ms / MINUTE);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${String(m)} min`;
  return m === 0 ? `${String(h)} h` : `${String(h)} h ${String(m)} min`;
}

/** slotId → the twin's detail; a slot whose shown time is its own is absent. */
export function twinDetails(
  slots: readonly { readonly slotId: string; readonly start: number; readonly end: number }[],
  shownTime: (ms: number) => string,
): Readonly<Record<string, string>> {
  const byTime = new Map<string, (typeof slots)[number][]>();
  for (const s of slots) {
    const key = shownTime(s.start);
    byTime.set(key, [...(byTime.get(key) ?? []), s]);
  }
  const out: Record<string, string> = {};
  for (const twins of byTime.values()) {
    if (twins.length < 2) continue;
    const lengths = twins.map((t) => durationWords(t.end - t.start));
    const apart = new Set(lengths).size === twins.length;
    twins.forEach((t, i) => {
      out[t.slotId] = apart ? lengths[i]! : `${String(i + 1)} of ${String(twins.length)}`;
    });
  }
  return out;
}

/** The props a PickerTile spreads: the detail and a name that says it (time, detail, note). */
export function twinTile(
  details: Readonly<Record<string, string>>,
  slotId: string,
  time: string,
  note?: string,
): { readonly detail: string; readonly label: string } | Record<string, never> {
  const detail = details[slotId];
  if (detail === undefined) return {};
  const spoken = /^\d+ of \d+$/.test(detail) ? `${detail} at this time` : detail;
  return { detail, label: [time, spoken, ...(note === undefined ? [] : [note])].join(", ") };
}
